import React, { useEffect, useRef } from 'react'
import { autorun } from 'mobx'
import { observer } from 'mobx-react-lite'
import { MDBIcon, MDBBtn } from 'mdb-react-ui-kit'
import CustomAlert from './../../shared/CustomAlert'
import Store from './AgentPushBanner.store'

import './AgentPushBanner.component.scss'

/**
 * Persistent in-app banner for unacknowledged CRITICAL messages from BST.
 *
 * This is what makes severity mean something: a standard message gets the bell
 * and the inbox, a critical one also sits at the top of every page until the
 * agent acknowledges it. Dismissing IS acknowledging — there is no way to hide
 * it while leaving it unread, because the acknowledgement is the only signal
 * the sender gets that it landed.
 *
 * Deliberately styled `info`, not `danger`. Red in this app already means
 * "your contract or billing is broken"; a message from the office is a notice.
 */
const AgentPushBanner = () => {
  const headingRef = useRef(null)
  const announcedRef = useRef(null)

  // MobX reactivity, not React deps: the bell's fetch populates UserAlertsStore
  // asynchronously, so this has to re-evaluate when that observable changes.
  // Expressing it as an autorun (mount-only effect) is both correct and honest
  // about what actually drives it.
  useEffect(
    () =>
      autorun(() => {
        if (Store.hasUnresolved) Store.load()
      }),
    []
  )

  const alerts = Store.criticalAlerts
  const topAlertId = alerts.length ? alerts[0].id : null

  // Move focus to the banner the first time a given message appears, so screen
  // reader and keyboard users land on it rather than discovering it by chance.
  // Keyed on the id, not the array — the array is rebuilt every render.
  useEffect(() => {
    if (!topAlertId) return
    if (announcedRef.current === topAlertId) return
    announcedRef.current = topAlertId
    if (headingRef.current && typeof headingRef.current.focus === 'function') headingRef.current.focus()
  }, [topAlertId])

  if (!alerts.length) return null

  return (
    <div id='AgentPushBannerComponent'>
      {alerts.map(alert => {
        const message = Store.messageFor(alert)
        return (
          <CustomAlert color='info' key={`push-banner-${alert.id}`} role='alert'>
            <div className='push-banner-row'>
              <MDBIcon icon='bullhorn' className='push-banner-icon' />
              <div className='push-banner-copy'>
                <strong tabIndex='-1' ref={headingRef}>
                  {(message && message.title) || 'A message from USA Benefits Group'}
                </strong>
                <div>{alert.alert_message}</div>
              </div>
              <div className='push-banner-actions'>
                {message && message.link_url ? (
                  <MDBBtn size='sm' color='indigo' tag='a' href={message.link_url}>
                    Open
                  </MDBBtn>
                ) : null}
                <MDBBtn size='sm' outline color='indigo' onClick={() => Store.acknowledge(alert)}>
                  Got It
                </MDBBtn>
              </div>
            </div>
          </CustomAlert>
        )
      })}
    </div>
  )
}

export default observer(AgentPushBanner)
